import React, { useState, useMemo } from 'react';
import { SkeletonText } from '../components';
import { OrganizationSelector } from './OrganizationSelector';
import { MemberMultiSelect } from './MemberMultiSelect';
import { getErrorMessage } from '../utils/errorUtils';
import { useFeatures } from '../contexts/FeaturesContext';
import { ShareSheet } from './sharing';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { RadioGroup, RadioOption } from './ui';
import { useExistingSharesQuery, useCreateShareMutation, useRemoveShareMutation } from '../hooks/queries/useSharing';

export type ShareEntityType = 'journal' | 'scene' | 'media' | 'album';
export interface ShareInfo { id: number; sharedWithUserId: number; sharedWithUserName: string; sharedWithUserEmail?: string; sharedByUserName: string; permission: string; createdAt: string; }
export type SharePermission = 'view' | 'edit';

interface ShareModalProps { isOpen: boolean; onClose: () => void; entityType: ShareEntityType; entityId: string; entityTitle: string; onShareSuccess?: () => void; }

export const ShareModal: React.FC<ShareModalProps> = (props) => {
  const { features } = useFeatures();
  if (features.unifiedSharing) {
    return <ShareSheet entityType={props.entityType} entityId={props.entityId} entityTitle={props.entityTitle} mode="modal" isOpen={props.isOpen} onClose={props.onClose} onShareSuccess={props.onShareSuccess} showPermissions={props.entityType === 'scene'} />;
  }
  return <LegacyShareModal {...props} />;
};

const ENTITY_LABELS: Record<ShareEntityType, string> = { journal: 'journal entry', scene: 'scene', media: 'media item', album: 'album' };

const LegacyShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, entityType, entityId, entityTitle, onShareSuccess }) => {
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedMemberIds, setSelectedMemberIds] = useState<number[]>([]);
  const [selectedPermission, setSelectedPermission] = useState<SharePermission>('view');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const { data: existingShares, isLoading: sharesLoading } = useExistingSharesQuery(entityType, entityId, isOpen);
  const existingShareUserIds = useMemo(() => existingShares.map(s => s.sharedWithUserId), [existingShares]);
  const createShareMutation = useCreateShareMutation();
  const removeShareMutation = useRemoveShareMutation();
  const sharing = createShareMutation.isPending;
  const removingUserId = removeShareMutation.isPending ? removeShareMutation.variables?.userId : null;

  const handleShare = async () => {
    if (selectedMemberIds.length === 0) { setError('Please select at least one member to share with'); return; }
    if (!selectedOrgId) { setError('Please select a hierarchy'); return; }
    setError(''); setSuccess('');
    try {
      const orgIdNum = parseInt(selectedOrgId, 10);
      await createShareMutation.mutateAsync({
        entityType, entityId, memberIds: selectedMemberIds, permission: selectedPermission, orgId: orgIdNum,
      });
      setSuccess(`Shared with ${selectedMemberIds.length} member(s)`); setSelectedMemberIds([]);
      onShareSuccess?.();
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const handleRemoveShare = async (userId: number) => {
    setError('');
    try {
      await removeShareMutation.mutateAsync({ entityType, entityId, userId });
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const handleClose = () => { if (!sharing) { setSelectedMemberIds([]); setSelectedPermission('view'); setError(''); setSuccess(''); onClose(); } };
  const cascadeHint = entityType === 'journal' ? 'Sharing this entry will also share any linked scenes and media.' : entityType === 'album' ? 'Sharing this album will also share all media items in it.' : null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Share ${ENTITY_LABELS[entityType]}`} size="lg">
      <p className="text-sm text-text-secondary m-0 mb-1">Share <strong>"{entityTitle}"</strong> with hierarchy members</p>
      {cascadeHint && <p className="text-xs text-text-tertiary m-0 mb-4 italic">{cascadeHint}</p>}

      <div className="flex flex-col gap-4">
        <OrganizationSelector value={selectedOrgId} onChange={setSelectedOrgId} label="Hierarchy" disabled={sharing} className="mb-0" />
        {selectedOrgId && <MemberMultiSelect organizationId={selectedOrgId} value={selectedMemberIds} onChange={setSelectedMemberIds} excludeUserIds={existingShareUserIds} excludeSelf={true} label="Select Members" placeholder="Select members to share with..." disabled={sharing} className="mb-0" />}

        {entityType === 'scene' && selectedOrgId && (
          <RadioGroup
            name="permission"
            value={selectedPermission}
            onChange={(value) => setSelectedPermission(value as SharePermission)}
            disabled={sharing}
            label="Permission Level"
          >
            <RadioOption value="view" label="View Only" description="Can view this scene but not edit" />
            <RadioOption value="edit" label="Can Edit" description="Can view and edit this scene" />
          </RadioGroup>
        )}

        {error && <div className="bg-danger/10 border border-danger/20 text-danger py-2 px-3 rounded-lg text-sm" role="alert">{error}</div>}
        {success && <div className="bg-success/10 border border-success/20 text-success py-2 px-3 rounded-lg text-sm" role="status">{success}</div>}

        <Button variant="primary" onClick={handleShare} disabled={sharing || selectedMemberIds.length === 0 || !selectedOrgId} fullWidth>
          {sharing ? 'Sharing...' : `Share with ${selectedMemberIds.length} member(s)`}
        </Button>

        <div className="pt-4 border-t border-border-light">
          <h3 className="text-sm font-semibold text-text-primary m-0 mb-3">Currently Shared With</h3>
          {sharesLoading ? <SkeletonText lines={2} />
          : existingShares.length === 0 ? <p className="text-sm text-text-tertiary italic m-0">Not shared with anyone yet.</p>
          : (
            <ul className="m-0 p-0 list-none flex flex-col gap-1">
              {existingShares.map(share => (
                <li key={share.id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-bg-tertiary">
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-text-primary truncate">{share.sharedWithUserName}</span>
                    <span className="text-xs text-text-tertiary truncate">{share.sharedWithUserEmail}</span>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => handleRemoveShare(share.sharedWithUserId)} disabled={removingUserId === share.sharedWithUserId} aria-label="Remove share"
                    className="text-text-secondary hover:text-danger hover:bg-danger/10">
                    {removingUserId === share.sharedWithUserId ? '...' : '\u00D7'}
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="flex justify-end pt-4 mt-4 border-t border-border-light">
        <Button variant="secondary" onClick={handleClose} disabled={sharing}>Close</Button>
      </div>
    </Modal>
  );
};
